import React, { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import CardPage from './pages/CardPage';
import HomePage from './pages/HomePage';
import SeriesPage from './pages/SeriesPage';
import CollectionPage from './pages/CollectionPage';
import SetPage from './pages/SetPage';
import Auth from './components/Auth';
import EmailConfirmation from './pages/EmailConfirmation';
import ProfilePage from './pages/ProfilePage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ResetPassword from './pages/ResetPassword';
import CountrySetup from './pages/CountrySetup';
import CommunityPage from './pages/CommunityPage';
import PokéversePage from './pages/PokéversePage';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { initializeAuth } from './lib/supabase'; 
import { useCollectionStore } from './lib/collectionStore';
import { useCurrencyStore } from './lib/currencyStore';
import { ToastManager } from './components/Toast';
import './lib/amplify'; // Uncommented to enable Cognito authentication

function App() {
  const { updateExchangeRates } = useCurrencyStore();
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initStage, setInitStage] = useState<string>('Starting initialization...');

  useEffect(() => {
    // Initialize auth state and load essential data when app loads
    const init = async () => {
      try {
        // Initialize auth
        setInitStage('Initializing authentication...');
        await initializeAuth();
        
        // Load collection data
        setInitStage('Loading collection data...');
        await useCollectionStore.getState().loadCollection();
        
        // Initialize exchange rates
        setInitStage('Loading exchange rates...');
        await updateExchangeRates();
        
        setIsInitialized(true);
      } catch (err) {
        console.error('Initialization error:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize application');
        setIsInitialized(true); // Still set initialized to true so we render something
      }
    };
    
    init();
  }, [updateExchangeRates]);

  // Show loading state
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black">
        <div className="text-white text-xl mb-4">Loading...</div>
        <div className="text-white/60 text-sm">{initStage}</div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="max-w-md w-full p-6">
          <div className="text-red-500 text-xl mb-4">Error Loading Application</div>
          <div className="text-white/60 text-sm mb-4">{error}</div>
          <button 
            onClick={() => window.location.reload()}
            className="w-full py-2 px-4 bg-white/10 hover:bg-white/20 text-white rounded transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <ToastManager>
      <Routes>
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/callback" element={<EmailConfirmation />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/country-setup" element={<CountrySetup />} />
        <Route path="/" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="/pokeverse" element={<PokéversePage />} />
          <Route path="/:series" element={<SeriesPage />} />
          <Route path="/:series/:setName" element={<SetPage />} />
          <Route path="/:series/:setName/:cardName" element={<CardPage />} />
          <Route path="/collection" element={<CollectionPage />} />
          <Route path="/community-sentiment" element={<CommunityPage />} />
          <Route path="/profile" element={<ProfilePage />} />
        </Route>
      </Routes>
    </ToastManager>
  );
}

export default App;